import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import { Alert, FormHelperText, OutlinedInput, Stack } from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { IconButtonAnimate } from 'components/animate';
import { setUserRole } from 'external/firebase/controllers/auth';
import useLocales from 'hooks/useLocales';
import { PATH_DASHBOARD } from 'routes/paths';

// eslint-disable-next-line consistent-return
const maxLength = object => {
    if (object.target.value.length > object.target.maxLength) {
        // eslint-disable-next-line no-return-assign,no-param-reassign
        return (object.target.value = object.target.value.slice(0, object.target.maxLength));
    }
};

export interface VerifyCodeFormValues {
    code1: string;
    code2: string;
    code3: string;
    code4: string;
    code5: string;
    code6: string;
}

const VerifyCodeForm: React.FunctionComponent = () => {
    const navigate = useNavigate();
    const { translate } = useLocales();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const VerifyCodeSchema = Yup.object().shape({
        code1: Yup.string().required(translate('code_required')),
        code2: Yup.string().required(translate('code_required')),
        code3: Yup.string().required(translate('code_required')),
        code4: Yup.string().required(translate('code_required')),
        code5: Yup.string().required(translate('code_required')),
        code6: Yup.string().required(translate('code_required')),
    });

    const formik = useFormik({
        initialValues: {
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        },
        validationSchema: VerifyCodeSchema,
        onSubmit: async (
            values: VerifyCodeFormValues,
            { setSubmitting, setStatus, resetForm }: FormikHelpers<VerifyCodeFormValues>,
        ) => {
            const roleCode = values.code1 + values.code2 + values.code3 + values.code4 + values.code5 + values.code6;
            const res = await setUserRole(roleCode);
            setSubmitting(false);
            if (!res.ok) {
                resetForm();
                setStatus(translate(res.code));
                return;
            }
            navigate(PATH_DASHBOARD.general.modules, { replace: true });
            enqueueSnackbar(translate('register_success'), {
                variant: 'success',
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        },
    });

    const { values, errors, status, isValid, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue } =
        formik;

    const handlePasteClipboard = event => {
        let data = event?.clipboardData?.getData('Text') || '';

        data = data.split('');

        [].forEach.call(document.querySelectorAll('#field-code'), (node, index) => {
            const fieldIndex = `code${index + 1}`;
            setFieldValue(fieldIndex, data[index]);
        });

        const lastfield = document.querySelector(`input[name=code6]`);

        if (lastfield !== null) {
            // @ts-ignore
            lastfield.focus();
        }
    };

    const handleChangeWithNextField = (event, handleChange) => {
        const { maxLength, value, name } = event.target;
        const fieldIndex = name.replace('code', '');

        const fieldIntIndex = Number(fieldIndex);

        if (value.length >= maxLength) {
            if (fieldIntIndex < 6) {
                const nextfield = document.querySelector(`input[name=code${fieldIntIndex + 1}]`);

                if (nextfield !== null) {
                    // @ts-ignore
                    nextfield.focus();
                }
            }
        }

        handleChange(event);
    };

    const handleKeyDownWithPreviousField = event => {
        const { name, value } = event.target;
        const fieldIndex = name.replace('code', '');

        const fieldIntIndex = Number(fieldIndex);

        if (event.key === 'Backspace' || event.key === 'Delete') {
            if (fieldIntIndex !== 0 && (fieldIntIndex !== 6 || !value)) {
                const previousField = document.querySelector(`input[name=code${fieldIntIndex - 1}]`);

                if (previousField !== null) {
                    // @ts-ignore
                    previousField.focus();
                }
            }
        }
    };

    useEffect(() => {
        document.addEventListener('paste', handlePasteClipboard);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <FormikProvider value={formik}>
            <Form autoComplete='off' noValidate onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    {status && <Alert severity='error'>{status}</Alert>}
                    <Stack direction='row' spacing={2} justifyContent='center'>
                        {Object.keys(values).map((name, index) => (
                            <OutlinedInput
                                {...getFieldProps(name)}
                                key={name}
                                id='field-code'
                                name={`code${index + 1}`}
                                type='text'
                                autoFocus={index === 0}
                                placeholder='-'
                                onInput={maxLength}
                                onChange={event => handleChangeWithNextField(event, formik.handleChange)}
                                onKeyDown={handleKeyDownWithPreviousField}
                                error={Boolean(touched[name] && errors[name])}
                                inputProps={{
                                    maxLength: 1,
                                    sx: {
                                        p: 0,
                                        textAlign: 'center',
                                        width: { xs: 36, sm: 56 },
                                        height: { xs: 36, sm: 56 },
                                    },
                                }}
                            />
                        ))}
                    </Stack>

                    <FormHelperText error={!isValid} style={{ textAlign: 'right' }}>
                        {!isValid && translate('code_required')}
                    </FormHelperText>

                    <LoadingButton
                        fullWidth
                        size='large'
                        type='submit'
                        variant='contained'
                        loading={isSubmitting}
                        sx={{ mt: 3 }}
                    >
                        {translate('verify')}
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default VerifyCodeForm;
